'use client';

import { ApiAvailables, ApiScoreCard, ApiScoreCardv2, ISimulationScore } from '@/types';

/**
 * # Scoring Identity Policy
 *
 * Extracted from Phase 2 of the Scoring Domain Refactor Plan.
 *
 * ## Purpose
 * This module centralizes the logic for resolving score identity and scorecard relationships.
 * Previously, this logic was duplicated across multiple utilities:
 * - `get-certification-id-from-api-scorecard.ts`
 * - `map-simulation-with-history.ts`
 *
 * ## Responsibilities
 * 1. **Score Identification**: Generate canonical keys for scores to support upsert/deduplication
 * 2. **Scorecard Relationship Resolution**: Map API scorecards to simulations and certifications
 * 3. **API Versioning**: Handle both ApiScoreCard (v1, scenario_id) and ApiScoreCardv2 (simulation_id)
 *
 * ## Usage Context
 * - Store actions (addScore) use `getCanonicalScoreKey` to avoid duplicate score entries
 * - Mapping utilities (map-api-availables, map-api-simulations) use simulation ID resolution
 * - Feature hooks delegate certification lookup to this policy
 *
 * ## Key Design Decisions
 * - No backend communication; all logic is deterministic and synchronous
 * - Supports both v1 (scenario_id) and v2 (simulation_id) API scorecards transparently
 * - Null-safe with explicit early returns to avoid nested conditionals
 * - Intentional fallback priority order ensures consistent behavior across callsites
 */

// ================================
// Types
// ================================

/**
 * Union type for flexible scorecard handling.
 *
 * Allows functions to work with either API version without discriminating
 * at the call site. Discriminated union checks (e.g., 'scenario_id' in scorecard)
 * happen internally when needed.
 */
type AnyApiScorecard = ApiScoreCard | ApiScoreCardv2;

// ================================
// Identity Policy Functions
// ================================

/**
 * Generates a canonical key for score upsert and deduplication scenarios.
 *
 * ## Purpose
 * Produces a deterministic, unique identifier for a score object to enable
 * safe upsert operations in the store without creating duplicate entries
 * for the same logical score across different ID schemes.
 *
 * ## Priority Order
 * The key generation uses a strict priority order to ensure consistency:
 * 1. **sessionId** (primary)  — If present, always use session-based keying
 * 2. **uuid** (secondary)     — Fallback for scores without sessionId
 * 3. **id** (tertiary)        — Numeric id as last resort before unknown
 * 4. **unknown** (default)    — When no identifying fields are available
 *
 * This priority reflects common production patterns:
 * - Session-based scores (from completed roleplay attempts) are most common
 * - UUID-based scores (from persistent storage) are secondary
 * - Numeric id fallback handles edge cases during score processing
 *
 * ## Key Format
 * All keys are prefixed with their source type to avoid collisions:
 * - `session:401` (sessionId=401)
 * - `uuid:12345-abcde-67890` (uuid with that value)
 * - `id:42` (numeric id)
 * - `unknown` (no identifying data)
 *
 * ## Edge Cases Handled
 * - **Zero as sessionId**: Treated as valid (typeof check catches 0 as number)
 * - **Empty string UUID**: Falls through to id check (falsy string is skipped)
 * - **Missing fields**: Returns 'unknown' rather than throwing
 * - **Partial objects**: No assumption about field presence
 *
 * ## Example Usage
 * ```ts
 * const score = { sessionId: 401, uuid: 'some-uuid', ... };
 * const key = getCanonicalScoreKey(score); // 'session:401'
 *
 * // In store upsert:
 * const index = scores.findIndex(s => getCanonicalScoreKey(s) === key);
 * if (index >= 0) scores[index] = score; // update
 * else scores.push(score); // insert
 * ```
 *
 * @param score The score object to generate a key for
 * @returns A prefixed canonical key string, or 'unknown' if no identifying fields exist
 */
export const getCanonicalScoreKey = (score: ISimulationScore): string => {
  // ── Priority 1: sessionId ────────────────────────────────────────────────
  // Strict typeof check ensures 0 is treated as a valid sessionId, not falsy.
  if (typeof score.sessionId === 'number') {
    return `session:${score.sessionId}`;
  }

  // ── Priority 2: uuid ─────────────────────────────────────────────────────
  // Truthy check skips empty strings, null, undefined.
  if (score.uuid) {
    return `uuid:${score.uuid}`;
  }

  // ── Priority 3: id ──────────────────────────────────────────────────────
  // Numeric id as final fallback before unknown.
  if (typeof score.id === 'number') {
    return `id:${score.id}`;
  }

  // ── Default: unknown ────────────────────────────────────────────────────
  // No identifying data available; caller may need to retry or log warning.
  return 'unknown';
};

/**
 * Resolves simulation identifier from either API scorecard version.
 *
 * ## Purpose
 * Extracts the simulation/scenario ID from a scorecard, normalizing across
 * two incompatible API schemas (v1 and v2) into a single string identifier.
 * This is the first step in resolving scorecard → simulation → certification.
 *
 * ## API Versioning
 * - **ApiScoreCard (v1)**: Uses `scenario_id` (integer, sometimes nullable)
 * - **ApiScoreCardv2**: Uses `simulation_id` (integer or null)
 * Both represent the same logical concept (simulation/scenario used in the attempt).
 *
 * ## Discriminated Union Logic
 * Uses the `'scenario_id' in scorecard` check to determine which field to extract.
 * This avoids instanceof checks and works with plain objects (important for mocks/tests).
 *
 * ## Null Handling
 * - If the field is absent or null, returns null (not undefined, not empty string)
 * - Caller is responsible for handling null (typically early return)
 * - toString() conversion ensures consistent string format for downstream comparison
 *
 * ## Example Usage
 * ```ts
 * const v1: ApiScoreCard = { scenario_id: 123, ... };
 * resolveSimulationIdFromScorecard(v1); // '123'
 *
 * const v2: ApiScoreCardv2 = { simulation_id: 456, ... };
 * resolveSimulationIdFromScorecard(v2); // '456'
 *
 * const empty: ApiScoreCard = { scenario_id: null, ... };
 * resolveSimulationIdFromScorecard(empty); // null
 * ```
 *
 * @param scorecard Either an ApiScoreCard (v1) or ApiScoreCardv2
 * @returns Simulation ID as a string, or null if not available
 */
export const resolveSimulationIdFromScorecard = (
  scorecard: AnyApiScorecard,
): string | null => {
  // ── Check for v1 scorecard (has scenario_id field) ──────────────────────
  // Use 'in' operator to safely discriminate union without instanceof.
  if ('scenario_id' in scorecard) {
    // Convert to string for consistent comparison downstream; return null if missing.
    return scorecard.scenario_id?.toString() || null;
  }

  // ── Otherwise assume v2 scorecard (has simulation_id field) ──────────────
  // This is guaranteed because AnyApiScorecard is a union of v1|v2.
  // Convert to string and null-coalesce for consistency.
  return scorecard.simulation_id?.toString() || null;
};

/**
 * Resolves certification ID by matching a scorecard's simulation ID against
 * available certifications.
 *
 * ## Purpose
 * Given a scorecard from an API response, determine which certification it belongs to.
 * This is a critical relationship: scores → simulations → certifications for
 * proper data hierarchies in the UI (manager dashboards, sidebar hydration, etc.).
 *
 * ## Resolution Strategy
 * 1. Extract the simulation ID from the scorecard (delegates to resolveSimulationIdFromScorecard)
 * 2. Search availables for a certification that "owns" that simulation
 * 3. A certification owns a simulation if it appears in:
 *    - `certificationPractice` (the practice version)
 *    - `certificationSimulation` (the certification version)
 *    - `simulations` array (additional role-play attempts)
 *
 * ## Matching Logic
 * All three nodes are searched because a single simulation ID may appear in multiple
 * places depending on the certification design:
 * - Practice mode: practitioner attempts the scenario in lower-stakes mode
 * - Certification mode: practitioner attempts the same scenario for scoring/certification
 * - Additional simulations: related role-plays or prerequisite simulations
 *
 * All matches use string comparison after converting IDs to strings (nullable IDs are guarded).
 *
 * ## Return Semantics
 * - Returns the first matching certification's ID as a string
 * - Returns null if the simulation ID is missing from the scorecard
 * - Returns null if the simulation ID doesn't match any certification
 * - Returns null if availables array is empty or has null/missing simulation arrays
 * - Does NOT throw; defensive coding assumes incomplete data is possible
 *
 * ## Example Usage
 * ```ts
 * const scorecard: ApiScoreCard = { scenario_id: 101, ... };
 * const availables: ApiAvailables[] = [{
 *   id: 1,
 *   certificationPractice: { id: 101, ... }, // matches!
 *   certificationSimulation: { id: 102, ... },
 *   simulations: [],
 * }, ...];
 *
 * const certId = resolveCertificationIdFromScorecard(scorecard, availables);
 * // Returns '1'
 * ```
 *
 * ## Historical Context
 * This logic was previously duplicated in:
 * - `get-certification-id-from-api-scorecard.ts` (direct implementation)
 * - `map-simulation-with-history.ts` (inlined version)
 * Phase 2 consolidates it here as the single source of truth.
 *
 * @param scorecard Either an ApiScoreCard (v1) or ApiScoreCardv2
 * @param availables Array of available certifications with their simulation hierarchies
 * @returns The matching certification ID as a string, or null if not found
 */
export const resolveCertificationIdFromScorecard = (
  scorecard: AnyApiScorecard,
  availables: ApiAvailables[],
): string | null => {
  // ── Step 1: Resolve the simulation ID from the scorecard ──────────────
  // Handles v1 (scenario_id) and v2 (simulation_id) transparently.
  const simulationId = resolveSimulationIdFromScorecard(scorecard);

  // ── Step 2: Early exit if simulation ID is missing ───────────────────
  // Can't match a certification without knowing which simulation was attempted.
  if (!simulationId) {
    return null;
  }

  // ── Step 3: Search availables for a certification that owns this simulation
  // The find() call returns on first match; order is intentional (practice > cert > others).
  const matchingCertification = availables.find((available) => {
    // Check if the simulation appears as the certification's practice version
    const inPractice = available.certificationPractice?.id?.toString() === simulationId;

    // Check if the simulation appears as the certification's formal assessment version
    const inCertification = available.certificationSimulation?.id?.toString() === simulationId;

    // Check if the simulation appears in the array of additional role-plays
    // Each simulation's ID is converted to string for comparison (some may be null).
    const inSimulations = available.simulations?.some((simulation) => {
      return simulation.id?.toString() === simulationId;
    });

    // Return true if the simulation is found in any of the three locations.
    // Boolean() cast is defensive (some() returns true/false but others return undefined).
    return Boolean(inPractice || inCertification || inSimulations);
  });

  // ── Step 4: Return the matching certification's ID as a string ────────
  // If no match found, matchingCertification is undefined, so we return null.
  return matchingCertification?.id?.toString() || null;
};
